import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import { functions } from '../../lib/firebase';

/**
 * runAssignmentPlan callable hook (2026-09-04)。
 *
 * `useGenerateAssignments` と同 pattern (idle / pending / success / error)。
 * `dryRun: true` は書込ゼロで対象ごとの件数だけ返す (実行前の確認ダイアログが使う)。
 * 型は functions/src/verification/runAssignmentPlan.ts と手で同期する。
 */

export interface RunAssignmentPlanInput {
  districtId: string;
  planId: string;
  /** 「セッションごと」の予約で一部だけ実行するとき。省略 = 対象全部 */
  sessionIds?: string[];
  dryRun?: boolean;
}

export interface RunTargetResult {
  key: string;
  sessionId: string | null;
  sessionName: string | null;
  status: 'ok' | 'skipped' | 'failed';
  reason?: string;
  setId?: string;
  replacedExisting: boolean;
  published: boolean;
  posts: {
    total: number;
    eligible: number;
    excluded: { hidden: number; notAnalyzed: number; unlinked: number; outOfScope: number };
  };
  users: { total: number; eligible: number };
  assignmentCount: number;
  usersWithoutAssignment: number;
}

export interface RunAssignmentPlanResult {
  planId: string;
  dryRun: boolean;
  targets: RunTargetResult[];
}

export type UseRunAssignmentPlanState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: RunAssignmentPlanResult; error: null }
  | { status: 'error'; result: null; error: Error };

export interface UseRunAssignmentPlanReturn {
  state: UseRunAssignmentPlanState;
  call: (input: RunAssignmentPlanInput) => Promise<RunAssignmentPlanResult | null>;
  reset: () => void;
}

export function useRunAssignmentPlan(): UseRunAssignmentPlanReturn {
  const [state, setState] = useState<UseRunAssignmentPlanState>({
    status: 'idle',
    result: null,
    error: null,
  });

  async function call(input: RunAssignmentPlanInput): Promise<RunAssignmentPlanResult | null> {
    setState({ status: 'pending', result: null, error: null });
    try {
      const fn = httpsCallable<RunAssignmentPlanInput, RunAssignmentPlanResult>(
        functions,
        'runAssignmentPlan',
      );
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = e as Error;
      setState({ status: 'error', result: null, error: err });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle', result: null, error: null });
  }

  return { state, call, reset };
}
